import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from './config';

// People Collection Reference
const peopleCollection = collection(db, 'people');

// Get all people
export const getAllPeople = async () => {
  try {
    const snapshot = await getDocs(peopleCollection);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting people:', error);
    throw error;
  }
};

// Get single person
export const getPerson = async (code) => {
  try {
    const docRef = doc(db, 'people', code);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting person:', error);
    throw error;
  }
};

// Add person
export const addPerson = async (personData) => {
  try {
    const docRef = doc(db, 'people', personData.code);
    await setDoc(docRef, personData);
    return { id: personData.code, ...personData };
  } catch (error) {
    console.error('Error adding person:', error);
    throw error;
  }
};

// Update person
export const updatePerson = async (personData) => {
  try {
    const docRef = doc(db, 'people', personData.code);
    await updateDoc(docRef, personData);
    return { id: personData.code, ...personData };
  } catch (error) {
    console.error('Error updating person:', error);
    throw error;
  }
};

// Add transaction (manual) - writes to subcollection + updates store.bal in person doc
export const addTransaction = async (personId, storeCode, storeName, transaction) => {
  try {
    const txRef = collection(db, 'people', personId, 'transactions');
    const now = new Date();
    let txDate = now;
    if (transaction.date) {
      const parts = String(transaction.date).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (parts) txDate = new Date(parseInt(parts[3]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    const txData = {
      type: transaction.type || 'income',
      amount: transaction.amount,
      description: transaction.desc || transaction.description || 'Manual',
      date: Timestamp.fromDate(txDate),
      storeCode,
      storeName,
      source: 'manual',
      createdAt: serverTimestamp()
    };
    await addDoc(txRef, txData);

    const personRef = doc(db, 'people', personId);
    const personSnap = await getDoc(personRef);
    if (!personSnap.exists()) throw new Error('Person not found');
    const personData = personSnap.data();
    const stores = (personData.stores || []).map(s => {
      if ((s.storeCode || s.code) === storeCode) {
        const newBal = transaction.type === 'expense'
          ? (s.bal || 0) - transaction.amount
          : (s.bal || 0) + transaction.amount;
        return { ...s, bal: newBal };
      }
      return s;
    });
    await updateDoc(personRef, { stores });
    return { success: true };
  } catch (error) {
    console.error('Error adding transaction:', error);
    throw error;
  }
};

// Delete person
export const deletePerson = async (code) => {
  try {
    const docRef = doc(db, 'people', code);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting person:', error);
    throw error;
  }
};

// Update transaction - edits existing transaction in subcollection + updates store.bal
export const updateTransaction = async (personId, transactionId, storeCode, oldTransaction, newTransaction) => {
  try {
    // Update transaction document
    const txRef = doc(db, 'people', personId, 'transactions', transactionId);
    let txDate = new Date();
    if (newTransaction.date) {
      const parts = String(newTransaction.date).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (parts) txDate = new Date(parseInt(parts[3]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    
    const txData = {
      type: newTransaction.type,
      amount: newTransaction.amount,
      description: newTransaction.desc || newTransaction.description,
      date: Timestamp.fromDate(txDate)
    };
    
    await updateDoc(txRef, txData);

    // Update store balance
    const personRef = doc(db, 'people', personId);
    const personSnap = await getDoc(personRef);
    if (!personSnap.exists()) throw new Error('Person not found');
    
    const personData = personSnap.data();
    const stores = (personData.stores || []).map(s => {
      if ((s.storeCode || s.code) === storeCode) {
        let newBal = s.bal || 0;
        
        // Revert old transaction
        if (oldTransaction.type === 'expense') {
          newBal += oldTransaction.amount; // Add back
        } else {
          newBal -= oldTransaction.amount; // Subtract back
        }
        
        // Apply new transaction
        if (newTransaction.type === 'expense') {
          newBal -= newTransaction.amount;
        } else {
          newBal += newTransaction.amount;
        }
        
        return { ...s, bal: newBal };
      }
      return s;
    });
    
    await updateDoc(personRef, { stores });
    return { success: true };
  } catch (error) {
    console.error('Error updating transaction:', error);
    throw error;
  }
};